import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

// MUI
import { Login, Logout } from '@mui/icons-material';
import Box, { BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

// common
import MyLink from 'common/components/MyLink';
import useGeneralHook from 'common/hook/useGeneralHook';
import useIsGuest from 'common/hook/useIsGuest';
import { logoutFnc } from '@redux/authReducer';
import { AppState } from '@redux/store';
import { ROUTES_PATH } from 'layout/constants';
import ThemeSwitch from 'layout/main/ThemeSwitch';
// import LanguageSelect from "intl/component/LanguageSelect";

interface Props {
  sx?: BoxProps['sx'];
}

const ProfileBox = (props: Props) => {
  const { sx } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { dispatch, isMobile } = useGeneralHook();
  const userInfo = useSelector((state: AppState) => state.auth.userInfo);
  const isGuest = useIsGuest();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'userInfo-popover' : undefined;

  const logout = () => {
    dispatch(logoutFnc());
  };

  const name = useMemo(() => {
    if (!userInfo || !userInfo.userAuthen) return '';

    const { userAuthen } = userInfo;
    const emailOrName = userAuthen.email || userAuthen.unverifiedEmail || userInfo.name;

    return emailOrName || 'N/A';
  }, [userInfo]);

  return (
    <>
      <Button
        variant="outlined"
        className="items-center shrink-0 flex"
        sx={{
          gap: 0.5,
          display: 'flex',
          justifyContent: 'flex-start',
          textAlign: 'start',
          ...sx,
        }}
        style={{ gap: 8 }}
        onClick={handleClick}
      >
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography variant="body2" className="line-clamp-1">
            {isGuest ? <FormattedMessage id="guest" /> : name}
          </Typography>
        </Box>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={
          isMobile
            ? {
                vertical: 'top',
                horizontal: 'center',
              }
            : { vertical: 'bottom', horizontal: 'center' }
        }
        transformOrigin={
          isMobile
            ? {
                vertical: 'bottom',
                horizontal: 'center',
              }
            : undefined
        }
        className="mb-4 tablet:mt-2"
        PaperProps={{
          variant: 'outlined',
          elevation: 0,
          sx: {
            width: 260,
            p: 1,
            gap: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Khách: BE 403 mọi trang thông tin tài khoản (không nằm trong allowlist) — bỏ mục hồ sơ. */}
        {!isGuest && (
          <MyLink to={ROUTES_PATH.profile}>
            <Button
              variant="text"
              className="items-center shrink-0 flex"
              fullWidth
              title="profile"
              sx={{
                gap: 0.5,
                display: 'flex',
                justifyContent: 'flex-start',
                textAlign: 'start',
                ...sx,
              }}
              style={{ gap: 8 }}
            >
              <Box>
                <Typography variant="body2">{name}</Typography>
              </Box>
            </Button>
          </MyLink>
        )}
        {/* <LanguageSelect /> */}
        <ThemeSwitch />
        {/* Khách chưa có tài khoản nào để "đăng xuất" — việc họ cần là ĐĂNG NHẬP. Hành động vẫn là
            logoutFnc: xoá cookie khách + authOut() để ProtectedRoute tự đưa về /login (giống CTA ở
            Quick Search); nếu điều hướng tay sang /login thì RedirectRoute thấy isLogin vẫn true
            của token khách và đẩy ngược lại. */}
        <Button onClick={logout} fullWidth startIcon={isGuest ? <Login /> : <Logout />}>
          <FormattedMessage id={isGuest ? 'login' : 'logout'} />
        </Button>
      </Popover>
    </>
  );
};
export default ProfileBox;
